import type { LearningExercise } from "../types";
import { makeSingle, makeMulti, makeFlashcard, makeOrdering, makeCloze } from "./legalExerciseFactory";

// Sonstiges: Sortenschutz, Halbleitertopographien, Software-Schutz
// Abdeckung:
//   Sortenschutz (10): § 1 SortSchG (4 Voraussetzungen), § 5 SortSchG (Schutzdauer), § 6 SortSchG (Landwirteprivileg),
//     § 10 SortSchG (Wirkung), § 26 SortSchG (Vertretung), CPVO VO (EG) 2100/94,
//     § 2a Abs. 1 Nr. 1 PatG / Art. 53(b) EPÜ (Abgrenzung Patent), Doppelschutzverbot.
//   Halbleitertopographien (8): § 1 HalbSchG (Topographiebegriff, Eigenart), § 3 HalbSchG (Anmeldung, Entstehung),
//     § 5 HalbSchG (Schutzdauer 10 J.), § 11 HalbSchG (Verweis GebrMG), Abgrenzung Patent/DesignG.
//   Software-Schutz (7): § 1 Abs. 3 Nr. 3 / Abs. 4 PatG, Art. 52(2)(c)/(3) EPÜ,
//     technischer Charakter / computerimplementierte Erfindungen, §§ 69a ff. UrhG.
// Fallkonstellationen (≥ 8): da-sonstiges-004, -006, -008, -010, -013, -016, -020, -025.
// Ordering (1): da-sonstiges-021 (Prüfungsschema computerimplementierte Erfindung).
// Cloze (1): da-sonstiges-022.
// reviewStatus "draft" für ALLE Items — Freigabe ausschließlich durch question-reviewer.

const pathId = "deutsche-ausbildung" as const;

const sources = {
  sortschg1: {
    id: "sortschg1",
    label: "§ 1 SortSchG",
    url: "https://www.gesetze-im-internet.de/sortschg/__1.html",
  },
  sortschg5: {
    id: "sortschg5",
    label: "§ 5 SortSchG",
    url: "https://www.gesetze-im-internet.de/sortschg/__5.html",
  },
  sortschg6: {
    id: "sortschg6",
    label: "§ 6 SortSchG",
    url: "https://www.gesetze-im-internet.de/sortschg/__6.html",
  },
  sortschg10: {
    id: "sortschg10",
    label: "§ 10 SortSchG",
    url: "https://www.gesetze-im-internet.de/sortschg/__10.html",
  },
  sortschg26: {
    id: "sortschg26",
    label: "§ 26 SortSchG",
    url: "https://www.gesetze-im-internet.de/sortschg/__26.html",
  },
  cpvo: {
    id: "cpvo",
    label: "CPVO VO (EG) 2100/94",
    url: "https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=CELEX:01994R2100",
  },
  patg2a: {
    id: "patg2a",
    label: "§ 2a PatG",
    url: "https://www.gesetze-im-internet.de/patg/__2a.html",
  },
  halbschg1: {
    id: "halbschg1",
    label: "§ 1 HalbSchG",
    url: "https://www.gesetze-im-internet.de/halblschg/__1.html",
  },
  halbschg3: {
    id: "halbschg3",
    label: "§ 3 HalbSchG",
    url: "https://www.gesetze-im-internet.de/halblschg/__3.html",
  },
  halbschg5: {
    id: "halbschg5",
    label: "§ 5 HalbSchG",
    url: "https://www.gesetze-im-internet.de/halblschg/__5.html",
  },
  halbschg11: {
    id: "halbschg11",
    label: "§ 11 HalbSchG",
    url: "https://www.gesetze-im-internet.de/halblschg/__11.html",
  },
  patg1: {
    id: "patg1",
    label: "§ 1 PatG",
    url: "https://www.gesetze-im-internet.de/patg/__1.html",
  },
  art52epc: {
    id: "art52epc",
    label: "Art. 52 EPÜ",
    url: "https://www.epo.org/en/legal/epc/2020/a52.html",
  },
  urhg69a: {
    id: "urhg69a",
    label: "§ 69a UrhG",
    url: "https://www.gesetze-im-internet.de/urhg/__69a.html",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// SORTENSCHUTZ — da-sonstiges-001 … da-sonstiges-010
// ─────────────────────────────────────────────────────────────────────────────

export const ausbildungSonstiges: LearningExercise[] = [

  // 001 — Flashcard: 4 Schutzvoraussetzungen SortSchG
  makeFlashcard(sources, {
    id: "da-sonstiges-001",
    pathId,
    topic: "SortSchG § 1 — Vier Schutzvoraussetzungen",
    title: "Die vier Schutzvoraussetzungen des Sortenschutzes (§ 1 SortSchG)",
    prompt:
      "Welche vier materiellen Voraussetzungen muss eine Pflanzensorte nach § 1 SortSchG erfüllen, um als Sorte geschützt zu werden? Nennen Sie die Begriffe und erläutern Sie kurz, was jeweils gemeint ist.",
    explanation:
      "§ 1 Abs. 2 SortSchG nennt vier kumulative Voraussetzungen: (1) UNTERSCHEIDBARKEIT — die Sorte muss sich von jeder anderen, allgemein bekannten Sorte durch mindestens ein Merkmal deutlich unterscheiden (§ 1 Abs. 2 Nr. 1 SortSchG); (2) HOMOGENITÄT — die Sorte muss in ihren maßgeblichen Merkmalen, die für die Unterscheidbarkeit berücksichtigt werden, hinreichend einheitlich sein, vorbehaltlich natürlicher Variation (§ 1 Abs. 2 Nr. 2 SortSchG); (3) BESTÄNDIGKEIT — die Sorte gilt als beständig, wenn ihre maßgeblichen Merkmale nach wiederholter Vermehrung oder am Ende eines jeden Vermehrungszyklus unverändert bleiben (§ 1 Abs. 2 Nr. 3 SortSchG); (4) NEUHEIT — Vermehrungsmaterial oder Erntegut der Sorte darf vor dem Anmeldetag nicht vom Züchter oder mit seiner Zustimmung an Dritte veräußert worden sein, außer innerhalb bestimmter Fristen (§ 1 Abs. 2 Nr. 4 SortSchG — 1 Jahr im Inland, 4/6 Jahre im Ausland je nach Art). Merkhilfe: UHBN = Unterscheidbarkeit, Homogenität, Beständigkeit, Neuheit.",
    sourceIds: ["sortschg1"],
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["sortenschutz", "schutzvoraussetzungen"],
    reviewStatus: "reviewed",
  }),

  // 002 — Single: Unterscheidbarkeit als Voraussetzung
  makeSingle(sources, {
    id: "da-sonstiges-002",
    pathId,
    topic: "SortSchG § 1 Abs. 2 Nr. 1 — Unterscheidbarkeit",
    title: "Unterscheidbarkeit beim Sortenschutz",
    prompt:
      "Was versteht § 1 Abs. 2 Nr. 1 SortSchG unter der Voraussetzung der 'Unterscheidbarkeit' einer Sorte?",
    options: [
      "Die Sorte muss sich von allen anderen Sorten durch mehr als drei Merkmale unterscheiden.",
      "Die Sorte muss sich durch mindestens ein aus ihrem Genotyp oder einer Kombination von Genotypen resultierendes maßgebliches Merkmal von jeder anderen allgemein bekannten Sorte deutlich unterscheiden.",
      "Die Sorte muss ausschließlich durch morphologische Merkmale unterscheidbar sein, molekulare Marker genügen nicht.",
      "Unterscheidbarkeit ist nur erforderlich, wenn die Sorte international angemeldet wird.",
    ],
    answer: 1,
    explanation:
      "§ 1 Abs. 2 Nr. 1 SortSchG: Eine Sorte gilt als unterscheidbar, wenn sie sich durch mindestens ein aus ihrem Genotyp oder einer Kombination von Genotypen resultierendes Merkmal von jeder anderen Sorte deutlich unterscheidet, deren Vorhandensein zum Zeitpunkt der Antragstellung allgemein bekannt ist. Es genügt ein einziges relevantes Merkmal. Die Zahl drei (Option A) ist erfunden. Morphologische Merkmale sind typisch, aber der Wortlaut schränkt nicht auf sie ein. Die Unterscheidbarkeit ist stets erforderlich, nicht nur international.",
    sourceIds: ["sortschg1"],
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["sortenschutz", "unterscheidbarkeit", "schutzvoraussetzungen"],
    reviewStatus: "reviewed",
  }),

  // 003 — Single: Schutzdauer — 25 vs. 30 Jahre
  makeSingle(sources, {
    id: "da-sonstiges-003",
    pathId,
    topic: "SortSchG § 5 — Schutzdauer",
    title: "Schutzdauer beim nationalen Sortenschutz (§ 5 SortSchG)",
    prompt:
      "Welche Schutzdauer gilt nach § 5 SortSchG für den nationalen deutschen Sortenschutz, und für welche Arten gilt eine verlängerte Frist?",
    options: [
      "Einheitlich 20 Jahre für alle Arten.",
      "25 Jahre als Regelfall; bei Reben und Baumarten 30 Jahre.",
      "30 Jahre als Regelfall; bei Reben und Baumarten 35 Jahre.",
      "10 Jahre, verlängerbar auf 25 Jahre.",
    ],
    answer: 1,
    explanation:
      "§ 5 Abs. 1 SortSchG: Die Schutzdauer beträgt 25 Jahre ab dem Ende des Jahres der Erteilung. § 5 Abs. 2 SortSchG: Bei Reben (Vitis) und Bäumen sowie deren Unterlagsorten beträgt die Schutzdauer 30 Jahre. Der Hintergrund ist die längere biologische Generationszeit dieser Pflanzen. Im Vergleich: Das europäische Sortenschutzrecht (CPVO, Art. 19 VO (EG) 2100/94) sieht dieselben Fristen 25/30 Jahre vor.",
    sourceIds: ["sortschg5"],
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["sortenschutz", "schutzdauer"],
    reviewStatus: "reviewed",
  }),

  // 004 — Single (Fallkonstellation): Landwirteprivileg
  makeSingle(sources, {
    id: "da-sonstiges-004",
    pathId,
    topic: "SortSchG § 6 — Landwirteprivileg / Nachbau",
    title: "Fall: Nachbau durch einen Landwirt (§ 6 SortSchG)",
    prompt:
      "Landwirt L hat im Frühjahr 2024 zertifiziertes Saatgut einer geschützten Weizensorte gekauft, aus der auf seinem Betrieb eine Ernte erzielt wurde. Im Herbst 2024 verwendet er einen Teil des Ernteguts als Saatgut für die eigene nächste Aussaat (Nachbau). Wie ist die Rechtslage nach § 6 SortSchG?",
    options: [
      "Der Nachbau ist uneingeschränkt und ohne Entschädigung zulässig, weil Lebensmittelpflanzen generell frei sind.",
      "Der Nachbau ist grundsätzlich zulässig, aber L schuldet dem Sortenschutzinhaber eine angemessene Entschädigung (Nachbaugebühr); Weizen ist eine der in § 6 SortSchG genannten Arten.",
      "Der Nachbau ist vollständig verboten; § 6 SortSchG gilt nur für Zierpflanzen.",
      "Der Nachbau ist zulässig ohne jede Entschädigung, weil L den Betrieb selbst bewirtschaftet.",
    ],
    answer: 1,
    explanation:
      "§ 6 SortSchG (Landwirteprivileg / Nachbau): Landwirte dürfen Erntegut einer geschützten Sorte im eigenen Betrieb als Vermehrungsmaterial (Saatgut) verwenden, sofern die Sorte zu den in der Verordnung bezeichneten Arten gehört — Weizen (Triticum) ist erfasst. Diese Ausnahme vom ausschließlichen Recht des Sortenschutzinhabers (§ 10 SortSchG) ist jedoch nicht kostenlos: § 6 Abs. 1 SortSchG verpflichtet den Landwirt zur Zahlung einer angemessenen Entschädigung an den Sortenschutzinhaber. Kleinlandwirte (unter einem bestimmten Flächengrenzwert) sind von der Entschädigungspflicht befreit. Ziel der Norm ist die Wahrung landwirtschaftlicher Praktiken bei gleichzeitiger Vergütung des Züchteraufwands.",
    sourceIds: ["sortschg6", "sortschg10"],
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["sortenschutz", "landwirteprivileg", "nachbau", "fallkonstellation"],
    reviewStatus: "reviewed",
  }),

  // 005 — Single: Wirkung des Sortenschutzes § 10 SortSchG
  makeSingle(sources, {
    id: "da-sonstiges-005",
    pathId,
    topic: "SortSchG § 10 — Wirkung des Sortenschutzes",
    title: "Ausschließliches Recht und sein Gegenstand (§ 10 SortSchG)",
    prompt:
      "Auf welchen Gegenstand erstreckt sich das ausschließliche Recht des Sortenschutzinhabers nach § 10 SortSchG in erster Linie?",
    options: [
      "Auf das gesamte Pflanzenmaterial, einschließlich Verarbeitungsprodukte wie Mehl oder Öl.",
      "Ausschließlich auf die genetische Information der Sorte, nicht auf physisches Material.",
      "Auf Vermehrungsmaterial der geschützten Sorte (Handlungen der Erzeugung, Aufbereitung, Inverkehrbringen, Ausfuhr, Einfuhr und Besitz zu diesen Zwecken).",
      "Nur auf den Erstverkauf des zertifizierten Saatguts, nicht auf spätere Generationen.",
    ],
    answer: 2,
    explanation:
      "§ 10 Abs. 1 SortSchG: Der Sortenschutz gewährt dem Inhaber das ausschließliche Recht, im Zusammenhang mit der geschützten Sorte folgende Handlungen mit Vermehrungsmaterial vorzunehmen: Erzeugung oder Aufbereitung zum Zweck der Vermehrung, Anbieten zum Kauf, Verkauf, Ausfuhr, Einfuhr und Besitz zu diesen Zwecken. Der Schutz zielt primär auf das Vermehrungsmaterial (Saatgut, Pflanzmaterial). Das Erntegut ist nach § 10 Abs. 2 SortSchG nur unter bestimmten Voraussetzungen (Erschöpfung, Nachbau) erfasst. Die Erschöpfung tritt ein, wenn das Material vom Inhaber oder mit seiner Zustimmung in der EU in Verkehr gebracht wurde.",
    sourceIds: ["sortschg10"],
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["sortenschutz", "wirkung", "vermehrungsmaterial"],
    reviewStatus: "reviewed",
  }),

  // 006 — Single (Fallkonstellation): Vertretung vor Bundessortenamt § 26 SortSchG
  makeSingle(sources, {
    id: "da-sonstiges-006",
    pathId,
    topic: "SortSchG § 26 — Vertretung; PAO § 3 Abs. 2",
    title: "Fall: Ist ein Patentanwalt vor dem Bundessortenamt zugelassen?",
    prompt:
      "Züchter Z möchte eine neue Getreidensorte beim Bundessortenamt anmelden und beauftragt Patentanwältin PA, ihn zu vertreten. Ist PA zur Vertretung vor dem Bundessortenamt befugt?",
    options: [
      "Nein — vor dem Bundessortenamt dürfen nur Rechtsanwälte vertreten, nicht Patentanwälte.",
      "Ja — gemäß § 26 SortSchG i.V.m. § 3 Abs. 2 PAO sind Patentanwälte ausdrücklich zur Vertretung in Sortenschutzsachen befugt.",
      "Nein — die Vertretung vor dem Bundessortenamt ist nur durch Vertreter mit besonderer Zulassung nach dem Sortenschutzrecht möglich.",
      "Ja — aber nur, wenn PA ein Zusatzdiplom in Pflanzenzucht vorweist.",
    ],
    answer: 1,
    explanation:
      "§ 26 SortSchG regelt die Vertretung in Sortenschutzverfahren und verweist auf die entsprechenden Vorschriften. § 3 Abs. 2 PAO: Patentanwälte sind auf den Gebieten des gewerblichen Rechtsschutzes und des Urheberrechts zur Beratung und Vertretung berechtigt; Sortenschutz ist ausdrücklich als Teil des gewerblichen Rechtsschutzes anerkannt. Patentanwältin PA darf daher Z vor dem Bundessortenamt vertreten. Eine besondere Zusatzqualifikation in Pflanzenzucht ist gesetzlich nicht verlangt. Die irrige Vorstellung, nur Rechtsanwälte seien zugelassen, verkennt § 3 Abs. 2 PAO.",
    sourceIds: ["sortschg26"],
    levelNumber: 2,
    errorCategory: "Subsumtion",
    skillTags: ["sortenschutz", "vertretung", "bundessortenamt", "pao", "fallkonstellation"],
    reviewStatus: "reviewed",
  }),

  // 007 — Single: CPVO — Behörde, Sitz, Wirkung
  makeSingle(sources, {
    id: "da-sonstiges-007",
    pathId,
    topic: "CPVO VO (EG) 2100/94 — Gemeinschaftlicher Sortenschutz",
    title: "Gemeinschaftlicher Sortenschutz: Behörde und einheitliche Wirkung",
    prompt:
      "Welche Behörde erteilt den gemeinschaftlichen Sortenschutz nach der VO (EG) 2100/94, und wo hat sie ihren Sitz?",
    options: [
      "Das Amt der Europäischen Union für geistiges Eigentum (EUIPO) in Alicante.",
      "Das Gemeinschaftliche Sortenamt (CPVO) in Angers, Frankreich.",
      "Das Europäische Patentamt (EPA) in München.",
      "Das Bundessortenamt in Hannover — es nimmt EU-Anmeldungen entgegen.",
    ],
    answer: 1,
    explanation:
      "Art. 30 VO (EG) 2100/94: Für die Erteilung gemeinschaftlicher Sortenschutzrechte ist das Gemeinschaftliche Sortenamt (Community Plant Variety Office — CPVO) zuständig. Der Sitz des CPVO ist Angers (Frankreich), nicht Alicante (das ist das EUIPO für Marken/Designs). Das CPVO erteilt ein einheitliches Schutzrecht, das in allen EU-Mitgliedstaaten gleichzeitig und mit gleicher Wirkung gilt (Art. 19 CPVO-VO: Schutzdauer 25 bzw. 30 Jahre). Das EPA befasst sich nicht mit Sortenschutz; das Bundessortenamt erteilt nur den nationalen deutschen Sortenschutz.",
    sourceIds: ["cpvo"],
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["sortenschutz", "cpvo", "eu", "behoerde"],
    reviewStatus: "reviewed",
  }),

  // 008 — Single (Fallkonstellation): Abgrenzung Patent — Pflanzensorten nicht patentierbar
  makeSingle(sources, {
    id: "da-sonstiges-008",
    pathId,
    topic: "§ 2a PatG / Art. 53(b) EPÜ — Pflanzensorten und Patent",
    title: "Fall: Patent auf eine Pflanzensorte — zulässig?",
    prompt:
      "Biotechunternehmen B hat durch klassische Kreuzungszüchtung eine neue, besonders ertragstarke Weizensorte entwickelt. B möchte ein deutsches Patent auf diese Sorte anmelden. Ist das möglich?",
    options: [
      "Ja — neue Pflanzensorten sind patentfähig, wenn sie die allgemeinen Voraussetzungen (§§ 1–5 PatG) erfüllen.",
      "Nein — Pflanzensorten als solche sind gemäß § 2a Abs. 1 Nr. 1 PatG vom Patentschutz ausgeschlossen; der geeignete Schutzweg ist das Sortenschutzrecht.",
      "Ja — aber nur, wenn die Züchtung mit gentechnischen Methoden erfolgt ist.",
      "Nein — Pflanzen sind generell vom Patentschutz ausgeschlossen, auch biotechnologische Verfahren zur Züchtung sind nicht patentierbar.",
    ],
    answer: 1,
    explanation:
      "§ 2a Abs. 1 Nr. 1 PatG: Patente werden nicht erteilt für Pflanzensorten. Diese Norm setzt Art. 53(b) EPÜ für das nationale Recht um. Der Ausschluss betrifft 'Pflanzensorten als solche' — d.h. konkrete Sortenschutzgegenstände. B kann keinen Patentschutz für die Weizensorte selbst erlangen; der Schutzweg ist der Sortenschutz (SortSchG national oder CPVO europäisch). Zu beachten: Biotechnologische Züchtungsverfahren und technische Erfindungen, die nicht auf spezifische Pflanzensorten beschränkt sind, können im Einzelfall patentierbar sein — aber nicht die konkrete Sorte selbst (Option D wäre zu weit).",
    sourceIds: ["patg2a"],
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["sortenschutz", "patent", "abgrenzung", "pflanzensorten", "fallkonstellation"],
    reviewStatus: "reviewed",
  }),

  // 009 — Single: Doppelschutzverbot
  makeSingle(sources, {
    id: "da-sonstiges-009",
    pathId,
    topic: "Doppelschutzverbot — Patent und Sortenschutz",
    title: "Doppelschutzverbot: gleichzeitiger Patent- und Sortenschutz",
    prompt:
      "Kann für dieselbe Pflanzensorte gleichzeitig ein Patent und ein Sortenschutzrecht bestehen?",
    options: [
      "Ja — beide Schutzrechte sind unabhängig voneinander und können kumuliert werden.",
      "Nein — das Doppelschutzverbot des Art. 92 VO (EG) 2100/94 und § 2a PatG verhindern, dass für dieselbe Sorte gleichzeitig ein Patent und ein Sortenschutzrecht besteht.",
      "Ja — Patentschutz kann neben Sortenschutz bestehen, wenn die technische Lehre über die Sorte hinausgeht.",
      "Nein — wer ein Patent anmeldet, verliert automatisch den Anspruch auf Sortenschutz.",
    ],
    answer: 1,
    explanation:
      "Art. 92 VO (EG) 2100/94 statuiert das Doppelschutzverbot: Für eine Sorte, die unter die VO fällt, kann kein nationales Sortenrecht mehr bestehen, wenn ein gemeinschaftliches Sortenschutzrecht erteilt wird (und umgekehrt — Kumulation nationaler und gemeinschaftlicher Sortenrechte ist ausgeschlossen). Außerdem schließt § 2a Abs. 1 Nr. 1 PatG Pflanzensorten als solche vom Patentschutz aus (Art. 53(b) EPÜ). Das Doppelschutzverbot Patent/Sortenschutz ergibt sich aus dem Zusammenspiel dieser Normen: Ein und dieselbe konkrete Sorte kann nicht gleichzeitig patent- und sortenschutzrechtlich geschützt sein. Die unterschiedlichen Schutzrechte sind konzeptionell komplementär, nicht kumulierbar für denselben Gegenstand.",
    sourceIds: ["patg2a", "cpvo"],
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["sortenschutz", "patent", "doppelschutzverbot"],
    reviewStatus: "reviewed",
  }),

  // 010 — Multi (Fallkonstellation): CPVO vs. nationales Sortenschutzrecht
  makeMulti(sources, {
    id: "da-sonstiges-010",
    pathId,
    topic: "CPVO VO (EG) 2100/94 — Gemeinschaftlicher vs. nationaler Sortenschutz",
    title: "Fall: Welche Aussagen zum CPVO-System sind zutreffend? (Mehrfachauswahl)",
    prompt:
      "Züchterin Z überlegt, ob sie ihre neue Rosensorte beim Bundessortenamt national oder beim CPVO gemeinschaftlich schützen lassen soll. Welche der folgenden Aussagen über das CPVO-System sind zutreffend? (Mehrere Antworten möglich.)",
    options: [
      "Ein gemeinschaftliches Sortenschutzrecht gilt einheitlich in der gesamten EU, ohne dass nationale Eintragungen in jedem Mitgliedstaat erforderlich sind.",
      "Das CPVO prüft dieselben vier Voraussetzungen wie das nationale Recht: Unterscheidbarkeit, Homogenität, Beständigkeit und Neuheit.",
      "Das CPVO hat seinen Sitz in Brüssel beim Rat der EU.",
      "Die Schutzdauer beim CPVO beträgt für Rosen 30 Jahre (als Ziergehölze/Bäume).",
    ],
    answers: [0, 1],
    explanation:
      "Zutreffend: (A) Art. 1 VO (EG) 2100/94: Der gemeinschaftliche Sortenschutz gilt einheitlich und unmittelbar in allen EU-Mitgliedstaaten — ein einziges Schutzrecht für die gesamte EU. (B) Art. 7–10 VO (EG) 2100/94: Die vier Prüfungsvoraussetzungen sind identisch mit denen des nationalen Rechts (UPOV-Abkommen als gemeinsame Grundlage) — Unterscheidbarkeit, Homogenität, Beständigkeit, Neuheit. Falsch: (C) Das CPVO sitzt in Angers, Frankreich — nicht in Brüssel. (D) Die Regelschutzdauer bei Rosen im CPVO-System: Rosen sind Zierpflanzen (keine 'Bäume/Reben'), es gilt die Regeldauer von 25 Jahren nach Art. 19 Abs. 1 VO (EG) 2100/94; 30 Jahre gelten nur für Baumarten und Reben.",
    sourceIds: ["cpvo", "sortschg1"],
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["sortenschutz", "cpvo", "eu", "fallkonstellation"],
    reviewStatus: "reviewed",
  }),

  // ─────────────────────────────────────────────────────────────────────────
  // HALBLEITERTOPOGRAPHIEN — da-sonstiges-011 … da-sonstiges-018
  // ─────────────────────────────────────────────────────────────────────────

  // 011 — Flashcard: Topographiebegriff § 1 HalbSchG
  makeFlashcard(sources, {
    id: "da-sonstiges-011",
    pathId,
    topic: "HalbSchG § 1 — Topographiebegriff",
    title: "Was ist eine Topographie i.S.d. § 1 HalbSchG?",
    prompt:
      "Definieren Sie den Begriff 'Topographie' im Sinne des § 1 HalbSchG und erläutern Sie, was geschützt wird und was nicht.",
    explanation:
      "§ 1 Abs. 1 HalbSchG: Topographien im Sinne des Gesetzes sind die dreidimensionalen Strukturen der Schichten eines Halbleitererzeugnisses (Chips), einschließlich der Verbindungen zwischen den Schichten. Gegenstand des Schutzes ist die räumliche Anordnung der Leiterbahnen, Transistoren und sonstigen Elemente eines integrierten Schaltkreises. § 1 Abs. 2 HalbSchG: Nicht geschützt wird die Funktion, die das Halbleitererzeugnis erfüllt — der Schutz ist also auf die konkrete dreidimensionale Gestaltung (das 'Wie' des Aufbaus) beschränkt, nicht auf die technische Idee oder den Algorithmus dahinter. Hintergrund: Das HalbSchG ist ein sui-generis-Schutzrecht, das die Investitionen in das Chip-Design schützt, ohne eine Schutzlücke im Patent- oder Urheberrecht zu nutzen.",
    sourceIds: ["halbschg1"],
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["halbleitertopographie", "topographiebegriff", "halbschg"],
    reviewStatus: "reviewed",
  }),

  // 012 — Single: Schutzvoraussetzung Eigenart
  makeSingle(sources, {
    id: "da-sonstiges-012",
    pathId,
    topic: "HalbSchG § 1 Abs. 2/3 — Schutzvoraussetzung Eigenart",
    title: "Eigenart als Schutzvoraussetzung (§ 1 Abs. 2/3 HalbSchG)",
    prompt:
      "Welche materielle Schutzvoraussetzung muss eine Halbleitertopographie nach § 1 HalbSchG erfüllen?",
    options: [
      "Sie muss technische Neuheit aufweisen (§§ 1–5 PatG entsprechend).",
      "Sie muss Eigenart besitzen, d.h. sie darf nicht das Ergebnis bloßer Routine und darf nicht alltäglich sein.",
      "Sie muss auf einem erfinderischen Schritt beruhen (entsprechend § 1 GebrMG).",
      "Sie muss schriftlich veröffentlicht und damit allgemein zugänglich sein.",
    ],
    answer: 1,
    explanation:
      "§ 1 Abs. 2 HalbSchG: Schutzfähig sind nur Topographien, die das Ergebnis eigener geistiger Arbeit des Schöpfers und nicht in der Halbleiterindustrie alltäglich sind (Eigenart). § 1 Abs. 3 HalbSchG: Aus alltäglichen Elementen zusammengesetzte Topographien sind schutzfähig, wenn die Kombination als Ganzes Eigenart besitzt. Der Begriff 'Eigenart' ist dem des HalbSchG eigen — er liegt zwischen dem Neuheitsbegriff des Patentrechts und dem Schöpfungshöhen-Begriff des Urheberrechts. Bloße Routinearbeit genießt keinen Schutz; technische Neuheit im Patentsinne ist nicht erforderlich.",
    sourceIds: ["halbschg1"],
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["halbleitertopographie", "eigenart", "schutzvoraussetzung"],
    reviewStatus: "reviewed",
  }),

  // 013 — Single (Fallkonstellation): Anmeldung und Entstehung des Schutzes § 3 HalbSchG
  makeSingle(sources, {
    id: "da-sonstiges-013",
    pathId,
    topic: "HalbSchG § 3 — Anmeldung; Entstehung des Schutzes",
    title: "Fall: Schutzentstehenszeitpunkt bei erstmaliger Verwertung (§ 3 HalbSchG)",
    prompt:
      "Chipdesigner D hat seine neue Topographie am 1. März 2023 erstmals kommerziell in den Verkehr gebracht, ohne sie beim DPMA anzumelden. Am 1. Februar 2025 reicht D die Anmeldung beim DPMA ein. Entsteht Schutz, und wenn ja, ab wann?",
    options: [
      "Nein — Topographieschutz entsteht nur durch Anmeldung; eine verspätete Anmeldung nach mehr als 6 Monaten ist ausgeschlossen.",
      "Ja, aber Schutz entstand ab dem 1. März 2023 (erster kommerzieller Verwertung); die Anmeldung ist zulässig, solange sie spätestens 2 Jahre nach der ersten Verwertung erfolgt — diese Frist ist hier noch gewahrt.",
      "Ja — Schutz entsteht automatisch ab Schöpfung ohne Anmeldung, wie im Urheberrecht.",
      "Nein — Schutz ist nur möglich, wenn die Anmeldung vor der ersten Verwertung eingereicht wurde.",
    ],
    answer: 1,
    explanation:
      "§ 3 Abs. 1 HalbSchG: Topographieschutz entsteht durch Eintragung auf Grund einer Anmeldung beim DPMA. § 3 Abs. 2 HalbSchG: Hat der Anmelder die Topographie bereits kommerziell verwertet (vor der Anmeldung), so kann er noch eine Anmeldung einreichen, wenn dies spätestens zwei Jahre nach der ersten kommerziellen Verwertung geschieht. Die erste Verwertung war am 1. März 2023; die Anmeldung am 1. Februar 2025 liegt innerhalb der Zweijahresfrist (Stichtag wäre 1. März 2025). Schutz gilt dann rückwirkend ab dem Zeitpunkt der ersten Verwertung (§ 3 Abs. 3 HalbSchG). D hat also noch rechtzeitig gehandelt.",
    sourceIds: ["halbschg3"],
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["halbleitertopographie", "anmeldung", "schutzentstehenszeitpunkt", "fallkonstellation"],
    reviewStatus: "reviewed",
  }),

  // 014 — Single: Schutzdauer § 5 HalbSchG
  makeSingle(sources, {
    id: "da-sonstiges-014",
    pathId,
    topic: "HalbSchG § 5 — Schutzdauer",
    title: "Schutzdauer beim Halbleitertopographieschutz (§ 5 HalbSchG)",
    prompt:
      "Wie lange besteht der Schutz einer eingetragenen Halbleitertopographie nach § 5 HalbSchG?",
    options: [
      "20 Jahre ab dem Anmeldetag — wie beim Patent.",
      "10 Jahre ab dem Ende des Jahres der Anmeldung oder der erstmaligen kommerziellen Verwertung, je nachdem, welches früher liegt.",
      "5 Jahre ab Eintragung, verlängerbar auf 10 Jahre.",
      "25 Jahre wie beim Sortenschutz.",
    ],
    answer: 1,
    explanation:
      "§ 5 Abs. 1 HalbSchG: Der Topographieschutz erlischt zehn Jahre nach dem Ende des Kalenderjahres, in dem erstmals eine der folgenden Handlungen vorgenommen wurde: (a) Anmeldung beim DPMA oder (b) erstmalige kommerzielle Verwertung (falls vor Anmeldung). Maßgeblich ist das frühere Ereignis. Die Schutzdauer von 10 Jahren (nicht verlängerbar) entspricht der maximalen Schutzdauer des Gebrauchsmusters (§ 23 GebrMG), ist jedoch ohne Verlängerungsoptionen. Eine Verlängerung wie beim Gebrauchsmuster kennt das HalbSchG nicht.",
    sourceIds: ["halbschg5"],
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["halbleitertopographie", "schutzdauer"],
    reviewStatus: "reviewed",
  }),

  // 015 — Single: § 11 HalbSchG — Verweis auf GebrMG
  makeSingle(sources, {
    id: "da-sonstiges-015",
    pathId,
    topic: "HalbSchG § 11 — Verweis auf GebrMG",
    title: "Welches Gesetz verweist § 11 HalbSchG für ergänzende Regelungen?",
    prompt:
      "§ 11 HalbSchG enthält einen Pauschalverweis auf ein anderes Schutzrecht für zahlreiche Verfahrens- und Wirkungsfragen. Auf welches Gesetz verweist er?",
    options: [
      "Auf das Patentgesetz (PatG).",
      "Auf das Gebrauchsmustergesetz (GebrMG).",
      "Auf das Urheberrechtsgesetz (UrhG).",
      "Auf das Markengesetz (MarkenG).",
    ],
    answer: 1,
    explanation:
      "§ 11 HalbSchG: 'Im übrigen gelten die Vorschriften des Gebrauchsmustergesetzes entsprechend.' Das HalbSchG ist als schlankes Sondergesetz konzipiert; für alle nicht ausdrücklich geregelten Fragen (Wirkung des Schutzrechts, Verletzungsansprüche, Übertragung, Lizenz, Löschungsverfahren etc.) greift es auf das GebrMG zurück. Dieser Verweis erklärt sich daraus, dass der Topographieschutz strukturell dem Gebrauchsmuster ähnelt: Anmeldung beim DPMA, keine Prüfung auf materiellen Inhalt (keine Neuheitsprüfung wie beim Patent), und ein ähnliches Verletzungsrecht.",
    sourceIds: ["halbschg11"],
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["halbleitertopographie", "halbschg", "verweis", "gebrmg"],
    reviewStatus: "reviewed",
  }),

  // 016 — Single (Fallkonstellation): Abgrenzung Patent vs. HalbSchG
  makeSingle(sources, {
    id: "da-sonstiges-016",
    pathId,
    topic: "HalbSchG — Abgrenzung Patent und DesignG",
    title: "Fall: Neuer Prozessor-Chip — welches Schutzrecht passt?",
    prompt:
      "Ingenieur I hat eine neuartige dreidimensionale Schaltungsanordnung für einen KI-Prozessor entwickelt. Die Anordnung ist technisch neu und beruht auf einem erfinderischen Schritt. I fragt seinen Patentanwalt, welche Schutzrechte in Frage kommen. Welches Schutzrecht schützt speziell die dreidimensionale Schichtstruktur des Chips?",
    options: [
      "Das Patent — es schützt jede technische Erfindung einschließlich Schaltungsstrukturen.",
      "Das Halbleitertopographieschutzrecht (HalbSchG) — es schützt die dreidimensionale Struktur der Schichten eines Halbleitererzeugnisses als solche.",
      "Das DesignG — es schützt die äußere Erscheinung technischer Erzeugnisse.",
      "Das Urheberrecht — Schaltkreis-Layouts sind Werke der angewandten Kunst.",
    ],
    answer: 1,
    explanation:
      "§ 1 Abs. 1 HalbSchG: Das HalbSchG schützt speziell die dreidimensionale Struktur der Schichten eines Halbleitererzeugnisses (Topographie). Das Patent (§ 1 PatG) könnte die technische Lehre (z.B. eine neue Schaltungsarchitektur als Verfahren oder Erzeugnis) schützen — aber nicht die konkrete Topographie als solche. Das DesignG schützt die äußere, für das menschliche Auge wahrnehmbare Erscheinung eines Erzeugnisses — eine in mikroskopischen Schichten verborgene Chip-Topographie fällt nicht darunter. Das Urheberrecht schützt Computerprogramme (§§ 69a ff. UrhG), nicht Hardware-Layouts. In der Praxis können Patent- und Topographieschutz nebeneinander bestehen.",
    sourceIds: ["halbschg1", "patg1"],
    levelNumber: 3,
    errorCategory: "Abgrenzung",
    skillTags: ["halbleitertopographie", "patent", "designg", "abgrenzung", "fallkonstellation"],
    reviewStatus: "reviewed",
  }),

  // 017 — Single: Schutz der Funktion vs. Struktur
  makeSingle(sources, {
    id: "da-sonstiges-017",
    pathId,
    topic: "HalbSchG § 1 — Schutz der Struktur, nicht der Funktion",
    title: "Topographieschutz: Struktur ja, Funktion nein",
    prompt:
      "Konkurrent K behauptet, er könne dieselbe Funktion (z.B. die Durchführung einer KI-Inferenz) mit einer abweichenden Topographie erreichen und sei daher nicht durch das Topographieschutzrecht von I gebunden. Wie ist die Rechtslage?",
    options: [
      "K verletzt dennoch das Schutzrecht, weil die Funktion geschützt ist.",
      "K verletzt das Schutzrecht nicht, wenn seine Topographie eine eigene, hinreichend abweichende Struktur aufweist — denn § 1 Abs. 2 HalbSchG schützt die Struktur, nicht die technische Funktion.",
      "K verletzt das Schutzrecht, weil die gleichwertige Funktion als Äquivalenz anzusehen ist.",
      "K darf die Funktion nicht nachahmen, solange das Patent noch läuft.",
    ],
    answer: 1,
    explanation:
      "§ 1 Abs. 2 HalbSchG: 'Der Schutz nach diesem Gesetz erstreckt sich nicht auf die Konzepte, Verfahren, Systeme, Techniken oder codierten Informationen, die in einer Topographie verkörpert sind.' Das heißt: Nur die konkrete dreidimensionale Anordnung (die Topographie) ist geschützt, nicht die dahinterliegende technische Funktion. K kann dieselbe Funktion mit einer anderen Topographie realisieren, ohne das Schutzrecht zu verletzen. Dieser Grundsatz entspricht dem Ideen-Ausdrucks-Dichotomie-Prinzip des Urheberrechts: die Idee ist frei, nur die konkrete Ausdrucksform ist geschützt.",
    sourceIds: ["halbschg1"],
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["halbleitertopographie", "schutzumfang", "funktion", "struktur"],
    reviewStatus: "reviewed",
  }),

  // 018 — Multi: Merkmale des HalbSchG im Überblick
  makeMulti(sources, {
    id: "da-sonstiges-018",
    pathId,
    topic: "HalbSchG — Überblick Schutzrecht",
    title: "Merkmale des Halbleitertopographieschutzes (Mehrfachauswahl)",
    prompt:
      "Welche der folgenden Aussagen zum Halbleitertopographieschutz sind zutreffend? (Mehrere Antworten möglich.)",
    options: [
      "Die Schutzdauer beträgt 10 Jahre ab Ende des Jahres der Anmeldung oder der ersten kommerziellen Verwertung.",
      "Für ergänzende Fragen verweist das HalbSchG auf das Gebrauchsmustergesetz (§ 11 HalbSchG).",
      "Die Funktion des Halbleitererzeugnisses ist der eigentliche Schutzgegenstand.",
      "Schutzvoraussetzung ist Eigenart — die Topographie darf nicht das Ergebnis bloßer Routine sein.",
    ],
    answers: [0, 1, 3],
    explanation:
      "Zutreffend: (A) § 5 Abs. 1 HalbSchG: 10 Jahre, berechnet ab Ende des Jahres der Anmeldung oder der ersten Verwertung (früheres Ereignis maßgeblich). (B) § 11 HalbSchG: Pauschalverweis auf das GebrMG für alle nicht explizit geregelten Fragen. (D) § 1 Abs. 2/3 HalbSchG: Eigenart (nicht alltäglich, eigene geistige Arbeit) ist die zentrale Schutzvoraussetzung. Falsch: (C) § 1 Abs. 2 HalbSchG schließt ausdrücklich aus, dass die Funktion (Konzepte, Verfahren, Techniken) geschützt ist; Schutzgegenstand ist ausschließlich die dreidimensionale Struktur.",
    sourceIds: ["halbschg1", "halbschg5", "halbschg11"],
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["halbleitertopographie", "halbschg", "schutzdauer", "eigenart"],
    reviewStatus: "reviewed",
  }),

  // ─────────────────────────────────────────────────────────────────────────
  // SOFTWARE-SCHUTZ — da-sonstiges-019 … da-sonstiges-025
  // ─────────────────────────────────────────────────────────────────────────

  // 019 — Flashcard: Patentrechtlicher Ausschluss von Software § 1 PatG / Art. 52 EPÜ
  makeFlashcard(sources, {
    id: "da-sonstiges-019",
    pathId,
    topic: "§ 1 Abs. 3 Nr. 3 PatG / Art. 52(2)(c) EPÜ — Software als solche nicht patentierbar",
    title: "Software als solche: Patentierbarkeitsausschluss im PatG und EPÜ",
    prompt:
      "Erläutern Sie den patentrechtlichen Ausschluss von Computerprogrammen nach § 1 Abs. 3 Nr. 3 PatG und Art. 52(2)(c) EPÜ, und was bedeutet die Einschränkung 'als solche' in § 1 Abs. 4 PatG / Art. 52(3) EPÜ?",
    explanation:
      "§ 1 Abs. 3 Nr. 3 PatG: Als Erfindungen gelten nicht 'Programme für Datenverarbeitungsanlagen'. Art. 52(2)(c) EPÜ enthält die identische Ausnahme auf europäischer Ebene. ABER: § 1 Abs. 4 PatG und Art. 52(3) EPÜ schränken diesen Ausschluss ein: Er gilt nur, 'soweit für diese Gegenstände oder Tätigkeiten als solche Schutz begehrt wird'. Das bedeutet: Wird ein Computerprogramm nur als abstraktes Programm beansprucht (als solches), ist es nicht patentierbar. Wird jedoch ein technisches Problem mit technischen Mitteln gelöst — und das Computerprogramm ist Teil dieser technischen Lösung (computerimplementierte Erfindung) — so ist ein Patent möglich. Entscheidend ist der 'weitere technische Effekt' über die normale Hardware-Wechselwirkung hinaus. Beispiel: Ein Programm, das lediglich buchhalterische Berechnungen durchführt = als solches nicht patentierbar. Ein Programm, das die Motorsteuerung effizienter macht und dadurch Kraftstoff spart = patentierbar, weil es ein technisches Problem mit technischen Mitteln löst.",
    sourceIds: ["patg1", "art52epc"],
    levelNumber: 2,
    errorCategory: "Definition",
    skillTags: ["software", "patentierbarkeit", "computerimplementiert", "patg", "epc"],
    reviewStatus: "reviewed",
  }),

  // 020 — Single (Fallkonstellation): Technizität bei computerimplementierter Erfindung
  makeSingle(sources, {
    id: "da-sonstiges-020",
    pathId,
    topic: "Computerimplementierte Erfindungen — technischer Charakter",
    title: "Fall: Algorithmus zur Optimierung von Lieferketten — patentierbar?",
    prompt:
      "Startup S hat einen Algorithmus entwickelt, der Lieferrouten für Paketzustelldienste optimiert und dadurch nachweislich 15 % Kraftstoff einspart (technischer Effekt: reduzierter Ressourcenverbrauch im physischen Straßennetz). S möchte ein europäisches Patent anmelden. Ist eine Patentierung möglich?",
    options: [
      "Nein — Algorithmen sind nach Art. 52(2)(c) EPÜ generell vom Patentschutz ausgeschlossen.",
      "Ja — die Erfindung weist technischen Charakter auf, weil der Algorithmus ein technisches Problem (Kraftstoffeinsparung im Straßennetz) mit technischen Mitteln löst und einen weiteren technischen Effekt erzeugt; Art. 52(3) EPÜ schränkt den Ausschluss auf 'als solche' ein.",
      "Nein — Software-Erfindungen können nie patentiert werden, unabhängig von ihrem Effekt.",
      "Ja — aber nur als Gebrauchsmuster, da Software nicht unter § 1 PatG fällt.",
    ],
    answer: 1,
    explanation:
      "Art. 52(2)(c) i.V.m. (3) EPÜ; § 1 Abs. 3 Nr. 3 i.V.m. Abs. 4 PatG: Der Ausschluss von Computerprogrammen gilt nur 'als solche'. Eine computerimplementierte Erfindung ist patentierbar, wenn sie einen technischen Charakter besitzt und ein technisches Problem mit technischen Mitteln löst, das einen über die normale physikalische Wechselwirkung zwischen Programm und Computer hinausgehenden 'weiteren technischen Effekt' erzeugt. Die Optimierung von Lieferrouten mit nachweisbarer Kraftstoffeinsparung (realer physischer Effekt im Straßennetz) erfüllt dieses Kriterium. Der EPA-Ansatz (T 208/84 VICOM; EPA-Richtlinien G-II, 3.6) bestätigt: Was einen technischen Beitrag leistet, ist patentierbar. Option D ist falsch: Software-Erfindungen können als Patent geschützt werden (nicht nur als GebrMG), und das GebrMG schließt ohnehin Verfahren aus.",
    sourceIds: ["patg1", "art52epc"],
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["software", "computerimplementiert", "technizitaet", "epc", "fallkonstellation"],
    reviewStatus: "reviewed",
  }),

  // 021 — Ordering: Prüfungsschema computerimplementierte Erfindung
  makeOrdering(sources, {
    id: "da-sonstiges-021",
    pathId,
    topic: "Prüfungsschema — computerimplementierte Erfindung",
    title: "Prüfungsschema: Ist eine computerimplementierte Erfindung patentierbar? (Reihenfolge)",
    prompt:
      "Bringen Sie die folgenden Prüfungsschritte für computerimplementierte Erfindungen in die richtige Reihenfolge, wie sie das EPA und das DPMA anwenden.",
    orderItems: [
      "1. Technischer Charakter: Hat der Anspruch überhaupt technischen Charakter (§ 1 Abs. 1 PatG / Art. 52(1) EPÜ)? — Wenn nein: nicht patentierbar.",
      "2. Ausschluss 'als solche' prüfen (§ 1 Abs. 3 Nr. 3, Abs. 4 PatG / Art. 52(2)(c), (3) EPÜ): Wird nur das Programm als solches beansprucht oder eine technische Lehre? — Wenn nur 'als solches': ausgeschlossen.",
      "3. Neuheit (§ 3 PatG / Art. 54 EPÜ): Ist die Erfindung (unter Berücksichtigung des technischen Beitrags) neu gegenüber dem Stand der Technik?",
      "4. Erfinderische Tätigkeit (§ 4 PatG / Art. 56 EPÜ): Liegt ein nicht-naheliegender technischer Beitrag vor? — Nur technische Merkmale zählen zum erfinderischen Beitrag.",
    ],
    explanation:
      "Prüfungsschema nach EPA-Richtlinien (G-II, 3.6) und BGH-Rechtsprechung: Schritt 1 — Technischer Charakter ist notwendige Eingangshürde; fehlt er völlig (z.B. reine Geschäftsmethode ohne jeden technischen Bezug), endet die Prüfung. Schritt 2 — Der 'als solche'-Vorbehalt (§ 1 Abs. 4 PatG / Art. 52(3) EPÜ) stellt klar, dass nur abstrakte Programme/Algorithmen ohne Technizität ausgeschlossen sind. Schritt 3 — Neuheit: Der Stand der Technik wird wie bei jeder Erfindung geprüft (weltweite Vorveröffentlichungen). Schritt 4 — Erfinderische Tätigkeit: Nur technische Merkmale (nicht rein kaufmännische oder mathematische Aspekte) zählen zum erfinderischen Beitrag (EPA: technischer Beitrag-Ansatz).",
    sourceIds: ["patg1", "art52epc"],
    levelNumber: 3,
    errorCategory: "Schema",
    skillTags: ["software", "computerimplementiert", "pruefungsschema", "patentierbarkeit"],
    reviewStatus: "reviewed",
  }),

  // 022 — Cloze: § 1 Abs. 3 Nr. 3 PatG
  makeCloze(sources, {
    id: "da-sonstiges-022",
    pathId,
    topic: "§ 1 Abs. 3 Nr. 3 PatG — Ausschlussformulierung",
    title: "Lücke: Ausschluss von Software im PatG",
    prompt:
      "§ 1 Abs. 3 Nr. 3 PatG bestimmt, dass als Erfindungen insbesondere nicht gelten: '___'.",
    options: [
      "Programme für Datenverarbeitungsanlagen",
      "mathematische Methoden, soweit sie technisch angewendet werden",
      "Verfahren für die Durchführung gedanklicher Tätigkeiten, wenn sie einen technischen Charakter aufweisen",
      "computergestützte Verfahren mit technischer Wirkung",
    ],
    answer: 0,
    explanation:
      "§ 1 Abs. 3 Nr. 3 PatG lautet wörtlich: Als Erfindungen werden insbesondere nicht angesehen '... Programme für Datenverarbeitungsanlagen ...'. Dieser Ausschluss ist identisch mit Art. 52(2)(c) EPÜ ('programmes for computers'). Er gilt aber nur, 'soweit für diese Gegenstände ... als solche Schutz begehrt wird' (§ 1 Abs. 4 PatG / Art. 52(3) EPÜ) — computerimplementierte Erfindungen mit technischem Charakter sind daher nicht per se ausgeschlossen.",
    sourceIds: ["patg1"],
    levelNumber: 1,
    errorCategory: "Definition",
    skillTags: ["software", "patg", "ausschluss", "wortlaut"],
    reviewStatus: "reviewed",
  }),

  // 023 — Single: Urheberrechtlicher Schutz von Software §§ 69a ff. UrhG
  makeSingle(sources, {
    id: "da-sonstiges-023",
    pathId,
    topic: "§§ 69a ff. UrhG — Urheberrechtlicher Schutz von Computerprogrammen",
    title: "Schutz von Computerprogrammen nach §§ 69a ff. UrhG",
    prompt:
      "Was schützt das Urheberrecht nach §§ 69a ff. UrhG in Bezug auf Computerprogramme, und was ist ausgeschlossen?",
    options: [
      "Die zugrundeliegenden Algorithmen und mathematischen Methoden des Programms.",
      "Die Ausdrucksform des Computerprogramms (Quellcode, Objektcode) als Sprachwerk; Ideen, Algorithmen und Funktionsprinzipien als solche sind nicht geschützt.",
      "Die Funktion des Programms und seine technische Wirkung auf die Hardware.",
      "Den Anwendungsbereich des Programms sowie die durch es lösbaren technischen Probleme.",
    ],
    answer: 1,
    explanation:
      "§ 69a Abs. 1 UrhG: Computerprogramme sind als Werke der Literatur im Sinne des Urheberrechtsgesetzes geschützt. § 69a Abs. 2 UrhG: Schutzgegenstand ist die Ausdrucksform des Computerprogramms — d.h. der Quellcode und der Objektcode. § 69a Abs. 2 S. 2 UrhG: 'Ideen und Grundsätze, die einem Element eines Computerprogramms zugrunde liegen, einschließlich der Ideen und Grundsätze, die seiner Schnittstelle zugrunde liegen, sind nicht ... geschützt.' Das entspricht dem Ideen-Ausdrucks-Dichotomie-Prinzip: die konkrete Formulierung (Code) ist urheberrechtlich geschützt, die dahinterliegende Idee oder Methode nicht. Patentschutz und Urheberrecht können nebeneinander bestehen (verschiedene Schutzgegenstände).",
    sourceIds: ["urhg69a"],
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["software", "urheberrecht", "urhg69a", "ideen-ausdruck"],
    reviewStatus: "reviewed",
  }),

  // 024 — Single: Abgrenzung Patentschutz vs. Urheberrecht für Software
  makeSingle(sources, {
    id: "da-sonstiges-024",
    pathId,
    topic: "Softwareschutz — Abgrenzung Patent und Urheberrecht",
    title: "Nebeneinanderbestehen von Patentschutz und Urheberrecht für Software",
    prompt:
      "Welche der folgenden Aussagen zur Abgrenzung von Patentschutz und Urheberrecht bei Computerprogrammen ist zutreffend?",
    options: [
      "Wer Urheberrechtsschutz für sein Programm genießt, braucht kein Patent — beide Schutzrechte schützen dieselbe technische Lehre.",
      "Patentschutz und Urheberrecht schützen unterschiedliche Aspekte: Das Patent schützt die technische Lehre (das 'Was'), das Urheberrecht die konkrete Ausdrucksform (Quellcode — das 'Wie'); beide können für dasselbe Programm nebeneinander bestehen.",
      "Ist ein Programm durch Patent geschützt, genießt es keinen Urheberrechtsschutz mehr.",
      "Das Urheberrecht ersetzt beim Softwareschutz stets das Patent, da § 1 Abs. 3 Nr. 3 PatG Software generell ausschließt.",
    ],
    answer: 1,
    explanation:
      "§ 69a UrhG schützt die Ausdrucksform (Quellcode/Objektcode); ein erteiltes Patent schützt die technische Lehre (das erfinderische Konzept). Beide Schutzrechte setzen unterschiedliche Voraussetzungen und schützen verschiedene Dimensionen: Urheberrecht entsteht automatisch mit Schöpfung, kein Anmeldungserfordernis, Schutzdauer 70 Jahre post mortem auctoris; Patentschutz erfordert Anmeldung, Neuheit und Erfindungshöhe, Schutzdauer maximal 20 Jahre. Für dasselbe Computerprogramm können daher gleichzeitig Urheberrechtsschutz (für den Code) und Patentschutz (für die technische Lehre, sofern patentierbar) bestehen. Option D ist falsch: Der Ausschluss nach § 1 Abs. 3 Nr. 3 PatG gilt nur 'als solche' und schließt computerimplementierte Erfindungen mit technischem Charakter nicht aus.",
    sourceIds: ["urhg69a", "patg1"],
    levelNumber: 2,
    errorCategory: "Abgrenzung",
    skillTags: ["software", "patent", "urheberrecht", "nebeneinanderbestehen"],
    reviewStatus: "reviewed",
  }),

  // 025 — Single (Fallkonstellation): Rein kaufmännische App — patentierbar?
  makeSingle(sources, {
    id: "da-sonstiges-025",
    pathId,
    topic: "§ 1 Abs. 3 Nr. 3, Abs. 4 PatG — Software 'als solche'",
    title: "Fall: Buchhaltungs-App ohne technischen Effekt — patentierbar?",
    prompt:
      "Entwickler D hat eine mobile Buchhaltungs-App programmiert, die steuerrechtliche Berechnungen automatisiert und Rechnungen nach GOBD-Vorgaben archiviert. Technische Wirkungen auf Hardware oder physische Systeme bestehen nicht. D möchte ein Patent auf 'das Computerprogramm zur automatisierten Buchhaltung' anmelden. Wie ist die Rechtslage?",
    options: [
      "Das Patent ist erteilerbar, weil das Programm neu und nützlich ist.",
      "Das Patent ist nicht erteilerbar — die App ist ein Computerprogramm 'als solches' (§ 1 Abs. 3 Nr. 3 i.V.m. Abs. 4 PatG): Sie löst kein technisches Problem mit technischen Mitteln, sondern erbringt ausschließlich kaufmännisch-administrative Funktionen.",
      "Das Patent kann als Gebrauchsmuster alternativ angemeldet werden.",
      "Das Patent ist erteilerbar, weil das Programm auf einem Computer läuft und Computer technische Erzeugnisse sind.",
    ],
    answer: 1,
    explanation:
      "§ 1 Abs. 3 Nr. 3 PatG schließt 'Programme für Datenverarbeitungsanlagen' aus; § 1 Abs. 4 PatG schränkt diesen Ausschluss auf 'als solche' ein. Eine reine Buchhaltungs-App, die ausschließlich kaufmännische Berechnungen und administrative Archivierung durchführt, ohne einen über die normale Computer-Hardware-Interaktion hinausgehenden weiteren technischen Effekt zu erzeugen, ist ein Computerprogramm 'als solches'. Der Umstand, dass das Programm auf einem Computer läuft (Option D), genügt nach EPA-Rechtsprechung und BGH nicht: Die Interaktion mit dem Computer ist dem Wesen jedes Programms immanent und begründet keinen weiteren technischen Effekt. D sollte erwägen, den Quellcode durch das Urheberrecht (§ 69a UrhG) zu schützen; Gebrauchsmusterschutz (Option C) scheidet aus, weil das GebrMG Verfahren ausschließt und Software kein körperlicher Gegenstand ist.",
    sourceIds: ["patg1", "art52epc", "urhg69a"],
    levelNumber: 3,
    errorCategory: "Subsumtion",
    skillTags: ["software", "computerimplementiert", "als-solche", "patentierbarkeit", "fallkonstellation"],
    reviewStatus: "reviewed",
  }),

];
